import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import SystemDataService from '../../Services/SystemDataService';
import { getFaqData } from '../../Public/faq-data.js';

const ItemIcon = () => (
    <svg className="w-8 h-8 md:w-12 md:h-12 text-primary-300 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z" />
    </svg>
);

const AccordionItem = ({ item, isActive, onToggle }) => {
    const ref = useRef(null);
    return (
        <div className="border-b border-gray-200 last:border-0">
            <button
                onClick={() => onToggle(item.id)}
                aria-expanded={isActive}
                className="w-full flex items-start justify-between py-4 gap-4 text-left cursor-pointer"
            >
                <span className="text-sm font-bold text-primary-900 text-balance leading-snug">
                    {item.question}
                </span>
                <svg
                    className={`w-4 h-4 shrink-0 mt-0.5 text-primary-400 transition-transform duration-200 ${isActive ? 'rotate-180' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                >
                    <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                </svg>
            </button>
            <div
                ref={ref}
                style={{ maxHeight: isActive ? `${ref.current?.scrollHeight ?? 200}px` : '0px' }}
                className="overflow-hidden transition-all duration-300"
            >
                <p className="pb-4 text-sm text-primary-900/60 font-normal text-pretty leading-relaxed">
                    {item.answer}
                </p>
            </div>
        </div>
    );
};

const FaqSection = ({ faqCards: parentFaqCards }) => {
    const { language, dir } = useSelector(s => s.systemData || {});
    const txt = useMemo(() => getFaqData(language), [language]);

    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAll, setShowAll] = useState(false);
    const [activeId, setActiveId] = useState(null);

    useEffect(() => {
        let isMounted = true;
        const init = async () => {
            setLoading(true);
            const sanitize = (raw) => raw.map((c, i) => ({ ...c, id: c.id || `faq-${i}` }));
            if (parentFaqCards?.length > 0) {
                if (isMounted) { setCards(sanitize(parentFaqCards)); setLoading(false); }
                return;
            }
            try {
                const apiData = await SystemDataService.getCardFaq();
                if (apiData?.length > 0 && isMounted) {
                    setCards(sanitize(apiData));
                    setLoading(false);
                    return;
                }
            } catch (e) { console.error('FAQ Load Error:', e); }
            if (isMounted) { setCards(sanitize(txt.items || [])); setLoading(false); }
        };
        init();
        return () => { isMounted = false; };
    }, [parentFaqCards, txt.items]);

    if (!loading && cards.length === 0) return null;

    const previewItems = cards.slice(0, 2);
    const moreItems = cards.slice(2);

    return (
        <section dir={dir} className="w-full bg-gray-50 border-y border-gray-200">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6 md:py-10">

                {/* Header */}
                <div className="flex items-center justify-between mb-6 md:mb-10">
                    <div>
                        <h2 className="text-2xl md:text-4xl font-black text-primary-950 text-balance">
                            {txt.title || 'Frequently Asked Questions'}
                        </h2>
                        <div className="w-10 h-0.5 bg-secondary-500 mt-1.5" />
                    </div>
                    {!loading && moreItems.length > 0 && (
                        <button
                            onClick={() => setShowAll(v => !v)}
                            aria-expanded={showAll}
                            className="hidden md:flex text-secondary-500 text-sm md:text-base font-semibold whitespace-nowrap items-center gap-1 hover:text-secondary-600 transition-colors"
                        >
                            {showAll ? 'Show Less ↑' : 'View All FAQs >'}
                        </button>
                    )}
                </div>

                {/* Mobile: all items stacked */}
                {loading ? (
                    <div className="flex flex-col gap-4">
                        {[1, 2, 3, 4].map(i => (
                            <div key={i} className="h-24 bg-gray-200 rounded animate-pulse" />
                        ))}
                    </div>
                ) : (
                    <>
                        {/* Mobile: all items as accordion */}
                        <div className="flex flex-col md:hidden">
                            {cards.map(item => (
                                <AccordionItem
                                    key={item.id}
                                    item={item}
                                    isActive={activeId === item.id}
                                    onToggle={(id) => setActiveId(activeId === id ? null : id)}
                                />
                            ))}
                        </div>

                        {/* Desktop: 2 preview items in a row */}
                        <div className="hidden md:flex flex-row">
                            {previewItems.map((item, index) => (
                                <React.Fragment key={item.id}>
                                    <div className={`flex-1 flex flex-col items-start text-left gap-4 ${index === 0 ? 'pr-12' : 'pl-12'}`}>
                                        <ItemIcon />
                                        <p className="text-base font-bold text-primary-900 text-balance leading-snug">
                                            {item.question}
                                        </p>
                                        <p className="text-sm font-normal text-primary-900/60 text-pretty leading-relaxed line-clamp-3">
                                            {item.answer}
                                        </p>
                                    </div>
                                    {index < previewItems.length - 1 && (
                                        <div className="w-px bg-gray-200 self-stretch shrink-0" />
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    </>
                )}

                {/* Expanded accordion — desktop only */}
                {showAll && moreItems.length > 0 && (
                    <div className="hidden md:block mt-8 pt-6 border-t border-gray-200">
                        {moreItems.map(item => (
                            <AccordionItem
                                key={item.id}
                                item={item}
                                isActive={activeId === item.id}
                                onToggle={(id) => setActiveId(activeId === id ? null : id)}
                            />
                        ))}
                    </div>
                )}
            </div>

            <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'FAQPage',
                        mainEntity: cards.map(c => ({
                            '@type': 'Question',
                            name: c.question,
                            acceptedAnswer: { '@type': 'Answer', text: c.answer }
                        }))
                    })}
                </script>
            </Helmet>
        </section>
    );
};

export default FaqSection;
